import type { BackAcabadosInfo } from '@utils/conversion/getStandardMaterial'
import { validateHex } from '@utils/conversion/validateHex'
import {
  resolveFabricBumpScale,
  resolveFabricEnvMapIntensity,
  resolveFabricRoughness,
} from '@utils/sceneObjects/fabricTextureQuality'
import {
  applyTextureQuality,
  cleanTextureUrl,
  cloneCachedTexture,
  preloadTexture,
} from '@utils/sceneObjects/textureCache'
import type { MeshStandardMaterial, Texture } from 'three'
import * as THREE from 'three'

const NEUTRAL_ALBEDO = '#FFFFFF'

export type ZoneMaterialLike = {
  color?: string | number | null
  roughness?: number | null
  metalness?: number | null
  envMapIntensity?: number | null
  opacity?: number | null
  bumpScale?: number | null
  url?: string | null
  bumpMap?: string | null
}

type DiffuseTextureSource = {
  zoneName?: string
  url?: string | null
}

export function hasDiffuseTextureMap(
  zoneMaterial: ZoneMaterialLike | null | undefined,
  options: {
    zoneName?: string
    textures?: DiffuseTextureSource[]
    appliedMap?: Texture | null
  } = {},
): boolean {
  if (zoneMaterial?.url?.trim()) return true
  if (options.appliedMap) return true
  if (options.zoneName && options.textures?.length) {
    const zoneTexture = options.textures.find((texture) => texture.zoneName === options.zoneName)
    if (zoneTexture?.url?.trim()) return true
  }
  return false
}

export function normalizeZoneMaterialColor<T extends ZoneMaterialLike>(material: T): T {
  return {
    ...material,
    color: resolveApiTextureColor(material.color, Boolean(material.url?.trim())),
  }
}

function clamp01(value: number): number {
  return Math.max(0, Math.min(1, value))
}

export function resolveApiTextureColor(
  color: string | number | null | undefined,
  hasTextureMap = false,
): string {
  if (hasTextureMap) return NEUTRAL_ALBEDO

  if (color == null) return NEUTRAL_ALBEDO

  if (typeof color === 'string') {
    const trimmed = color.trim()
    if (validateHex(trimmed)) return trimmed
    return NEUTRAL_ALBEDO
  }

  if (typeof color === 'number' && Number.isFinite(color)) {
    const rgb = Math.max(0, Math.min(0xffffff, color >>> 0))
    return `#${rgb.toString(16).padStart(6, '0')}`
  }

  return NEUTRAL_ALBEDO
}

export function resolveMaterialRoughness(brillo?: number | null, fallback = 1): number {
  if (brillo == null || !Number.isFinite(brillo)) return clamp01(fallback)
  return clamp01(brillo)
}

export function resolveMaterialBumpScale(rugosidad?: number | null, fallback = 0): number {
  if (rugosidad == null || !Number.isFinite(rugosidad)) return Math.max(0, fallback)
  return Math.max(0, rugosidad)
}

export function resolveMaterialMetalness(metal?: number | null, fallback = 0): number {
  if (metal == null || !Number.isFinite(metal)) return clamp01(fallback)
  return clamp01(metal)
}

export function resolveMaterialEnvMapIntensity(reflejo?: number | null): number {
  if (reflejo == null || !Number.isFinite(reflejo)) return 0
  return Math.max(0, reflejo)
}

export function resolveZoneMaterialProps(
  zoneMaterial: ZoneMaterialLike | null | undefined,
  hasTextureMap = false,
  options: {
    zoneName?: string
    textures?: DiffuseTextureSource[]
    appliedMap?: Texture | null
  } = {},
) {
  const hasMap =
    hasTextureMap ||
    hasDiffuseTextureMap(zoneMaterial, {
      zoneName: options.zoneName,
      textures: options.textures,
      appliedMap: options.appliedMap,
    })
  const opacity =
    zoneMaterial?.opacity != null && Number.isFinite(zoneMaterial.opacity)
      ? clamp01(zoneMaterial.opacity)
      : 1

  return {
    color: resolveApiTextureColor(zoneMaterial?.color, hasMap),
    roughness: resolveMaterialRoughness(zoneMaterial?.roughness, 1),
    metalness: resolveMaterialMetalness(zoneMaterial?.metalness, 0),
    envMapIntensity: resolveMaterialEnvMapIntensity(zoneMaterial?.envMapIntensity),
    opacity,
    bumpScale: resolveMaterialBumpScale(zoneMaterial?.bumpScale, 0),
  }
}

function syncDetailMapTransform(detailTexture: Texture, diffuseTexture: Texture): void {
  detailTexture.wrapS = diffuseTexture.wrapS
  detailTexture.wrapT = diffuseTexture.wrapT
  detailTexture.repeat.copy(diffuseTexture.repeat)
  detailTexture.offset.copy(diffuseTexture.offset)
  detailTexture.rotation = diffuseTexture.rotation
  detailTexture.center.copy(diffuseTexture.center)
  detailTexture.needsUpdate = true
}

export function applyZoneMaterialToMesh(
  material: MeshStandardMaterial,
  zoneMaterial: ZoneMaterialLike | null | undefined,
  options: { hasTextureMap?: boolean; isFabric?: boolean } = {},
): void {
  const hasMap = Boolean(options.hasTextureMap ?? material.map)
  const resolved = resolveZoneMaterialProps(zoneMaterial, hasMap)

  material.roughness = options.isFabric
    ? resolveFabricRoughness(resolved.roughness)
    : resolved.roughness
  material.metalness = resolved.metalness
  material.envMapIntensity = options.isFabric
    ? resolveFabricEnvMapIntensity(resolved.envMapIntensity)
    : resolved.envMapIntensity
  material.opacity = resolved.opacity
  material.bumpScale = options.isFabric
    ? resolveFabricBumpScale(resolved.bumpScale, Boolean(zoneMaterial?.bumpMap))
    : resolved.bumpScale
  material.transparent = resolved.opacity < 1
  material.depthWrite = resolved.opacity >= 1

  if (typeof material.color?.set === 'function') {
    material.color.set(resolved.color)
  }

  material.needsUpdate = true
}

export function applyZoneDetailMapToMesh(
  material: MeshStandardMaterial,
  zoneMaterial: ZoneMaterialLike | null | undefined,
  diffuseTexture?: Texture | null,
  isFabric = false,
): void {
  const detailUrl = cleanTextureUrl(zoneMaterial?.bumpMap)
  if (!detailUrl) {
    material.bumpMap = null
    material.needsUpdate = true
    return
  }

  const assignDetail = (detailTexture: Texture) => {
    applyTextureQuality(detailTexture, { colorSpace: THREE.NoColorSpace })
    if (diffuseTexture) {
      syncDetailMapTransform(detailTexture, diffuseTexture)
    }

    const bumpScale = resolveMaterialBumpScale(zoneMaterial?.bumpScale, 0)
    material.bumpMap = detailTexture
    material.bumpScale = isFabric ? resolveFabricBumpScale(bumpScale, true) : bumpScale
    material.needsUpdate = true
  }

  const cached = cloneCachedTexture(detailUrl)
  if (cached) {
    assignDetail(cached)
    return
  }

  void preloadTexture(detailUrl).then((detailTexture) => {
    if (!detailTexture) return
    assignDetail(detailTexture)
  })
}

export function applyZoneBumpMapToMesh(
  material: MeshStandardMaterial,
  zoneMaterial: ZoneMaterialLike | null | undefined,
  diffuseTexture?: Texture | null,
): void {
  applyZoneDetailMapToMesh(material, zoneMaterial, diffuseTexture)
}

export function mapApiTexturasToMaterialFields(texturas?: BackAcabadosInfo | null) {
  const hasMap = Boolean(texturas?.url)
  return {
    color: resolveApiTextureColor(texturas?.color, hasMap),
    roughness: resolveMaterialRoughness(texturas?.brillo, 1),
    bumpScale: resolveMaterialBumpScale(texturas?.rugosidad, 0),
    metalness: resolveMaterialMetalness(texturas?.metal, 0),
    envMapIntensity: resolveMaterialEnvMapIntensity(texturas?.reflejo),
    opacity: texturas?.opacidad != null ? clamp01(texturas.opacidad) : 1,
    repeatX: texturas?.factorRepeticionX || 1,
    repeatY: texturas?.factorRepeticionY || 1,
    textureRotation: texturas?.orientacionTextura ?? 0,
    mirror: texturas?.espejo ?? false,
    bumpMap: texturas?.rugosidadFichero,
    displacement: texturas?.desplazamiento ?? 0,
    displacementScale: texturas?.factorDeEscala,
  }
}
